import { useEffect, useState } from 'react';
import { XIcon } from '@phosphor-icons/react';
import { Button, Modal, SearchField } from '@skupreme/shared/ui';
import SearchResults from '@components/GlobalSearch/SearchResults';
import { useGlobalSearchResults } from '@components/GlobalSearch/use-search-results';
import { useDebounce } from '@hooks/debounce';
import { CommonUtility } from '@utility/common';

const NOTE =
  'sk:flex sk:h-15 sk:items-center sk:justify-center sk:text-sm sk:font-semibold sk:text-fg-secondary';

type DialogBoxProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

function DialogBox({ open, setOpen }: DialogBoxProps) {
  const [search, setSearch] = useState('');

  const finalSearch = useDebounce(search, 400);
  const { loading, data } = useGlobalSearchResults(finalSearch);

  useEffect(() => {
    setSearch('');
  }, [open]);

  const searchInvalid = (search || '').trim().length < 3;

  const renderContent = () => {
    // Search Keyword is Invalid
    if (searchInvalid) {
      return <div className={NOTE}>Please type 3 or more characters</div>;
    }

    // API is in Pending state
    if (loading) {
      return <div className={NOTE}>Loading...</div>;
    }

    // Received 0 results from API
    if (!CommonUtility.isValidArray(data)) {
      return <div className={NOTE}>No Data Found</div>;
    }

    // Received Valid Response
    if (CommonUtility.isValidArray(data)) {
      return data.map(d => (
        <SearchResults
          key={d.model}
          type={d.model}
          data={d.response}
          searchTerm={finalSearch}
        />
      ));
    }

    return null;
  };

  return (
    <Modal
      isOpen={open}
      onOpenChange={setOpen}
      size="lg"
      aria-label="Search"
      /* Pinned near the top, so the field stays put as results come and go. */
      className="sk:mt-[10vh] sk:self-start sk:overflow-hidden"
      bodyClassName="sk:p-0"
    >
      <div className="sk:flex sk:items-center sk:gap-2 sk:border-b sk:border-rule sk:p-3">
        <SearchField
          autoFocus
          value={search}
          onChange={setSearch}
          isLoading={loading}
          placeholder="Search..."
          className="sk:h-9 sk:w-auto sk:flex-1"
        />
        <Button
          variant="ghost"
          shape="circle"
          aria-label="Close"
          onPress={() => setOpen(false)}
        >
          <XIcon size={16} />
        </Button>
      </div>
      {/* A click on a result navigates, then this closes the dialog. */}
      <div
        className="sk:max-h-[50vh] sk:min-h-15 sk:overflow-auto"
        onClick={() => setOpen(false)}
      >
        {renderContent()}
      </div>
    </Modal>
  );
}

DialogBox.displayName = 'GlobalSearchDialogBox';

export default DialogBox;
